import {MetaTags} from 'components/seo';
import {Button} from 'components/system/button';
import {Divider} from 'components/system/divider';
import {Sticky} from 'components/system/sticky';
import {TextInput} from 'components/system/text_input';
import {useIsMobile} from 'hooks/is_mobile';
import {useLocalStorageState} from 'hooks/local_storage_state';
import {useQueryStringState} from 'hooks/query_string_state';
import {SearchIcon} from 'icons';
import {useEffect, useMemo, useState} from 'react';
import {useAuth} from 'users';
import {applyQuestionListConfig, defaultQuestionListConfig} from 'utils/question_list';
import {RecommendedQuestions} from '../recommended_questions';
import {Categories} from './categories';
import {QuestionCounts} from './question_counts';
import styles from './question_list.module.css';
import {QuestionTable} from './question_table';
import {QuestionTablePaginator} from './question_table_paginator';
import {QuickFilters} from './quick_filters';
import {SubmitQuestionButton} from './submit_question_button';
import {Toolbar} from './toolbar';
import {TopCompanies} from './top_companies';
import {TopQuestionTags} from './top_questions_tags';
import {QuestionWeekly} from './weekly_goal';

export const QuestionList = ({questions: initialQuestions, categories}: Props) => {
  const isMobile = useIsMobile();
  const [questions, setQuestions] = useState(initialQuestions);
  const {isReady, hasPermission} = useAuth();

  const [cachedFilters, setCachedFilters] = useLocalStorageState<{}>(
    'QUESTIONS_FILTERS2',
    defaultQuestionListConfig,
  );

  const [config, setConfig] = useQueryStringState(defaultQuestionListConfig, {
    numberFields: ['levels', 'page', 'pageSize'],
  });

  useEffect(() => {
    setCachedFilters(config);
  }, [config]);

  useEffect(() => {
    // Only use cached filters if we don't have explicit filters in the querystring on page load.
    if (!window.location.search) setConfig(cachedFilters);
  }, []);

  useEffect(() => {
    if (isReady && !hasPermission('question_company_filter')) {
      setConfig({...config, companies: []});
    }
  }, [isReady, !hasPermission('question_company_filter')]);

  const [filteredQuestions, paginatedQuestions] = useMemo(
    () => applyQuestionListConfig(questions, config),
    [questions, config],
  );

  const handleQuestionChange = (newQuestion: Question) => {
    setQuestions(
      questions.map((question) => (question.uuid === newQuestion.uuid ? newQuestion : question)),
    );
  };
  const clearFilters = () => {
    setConfig(defaultQuestionListConfig);
  };
  const hasFilters = useMemo(() => {
    const discardFilters = ['page', 'pageSize', 'orderingDirection'];
    const filters = Object.entries(config).map(([key, value]) => {
      if (discardFilters.includes(key)) {
        return false;
      } else if (key === 'ordering' && value === defaultQuestionListConfig.ordering) {
        return false;
      } else if (typeof value === 'string' && value.length > 0) {
        return true;
      } else if (typeof value === 'number' && value > 0) {
        return true;
      } else if (Array.isArray(value) && value.length > 0) {
        return true;
      }
      return false;
    });
    return filters.some(Boolean);
  }, [config]);

  const pageTitle = useMemo(() => {
    const title = [];
    if (config.companies.length === 1) {
      title.push(config.companies[0]);
    } else if (config.positions.length === 1) {
      title.push(config.positions[0]);
    }
    title.push('Interview Questions');
    return title.join(' ');
  }, [config]);

  return (
    <>
      <MetaTags
        title={pageTitle}
        canonical='/questions'
        prev={
          config.page > 0 ? `/questions?page=${config.page - 1}&pageSize=${config.pageSize}` : ''
        }
        next={
          config.page < Math.floor(questions.length / config.pageSize)
            ? `/questions?page=${config.page + 1}&pageSize=${config.pageSize}`
            : ''
        }
      />
      <div className={styles.header}>
        <h1>{pageTitle}</h1>
        <SubmitQuestionButton />
      </div>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          {isMobile && (
            <QuestionCounts questions={questions} visibleQuestions={filteredQuestions} />
          )}
          <Categories categories={categories} config={config} onConfigChange={setConfig} />
          <Sticky shadow={true} disabled={isMobile} zIndexOffset={1}>
            <div className={styles.toolbar}>
              <Toolbar
                categories={categories}
                questions={questions}
                hasFilters={hasFilters}
                clearFilters={clearFilters}
                config={config}
                onConfigChange={setConfig}
              />
            </div>
          </Sticky>
          {paginatedQuestions.length > 0 ? (
            <>
              <QuestionTable
                questions={paginatedQuestions}
                onQuestionChange={handleQuestionChange}
                config={config}
                onConfigChange={setConfig}
              />
              <QuestionTablePaginator
                questions={filteredQuestions}
                config={config}
                onConfigChange={(config) => {
                  setConfig(config);
                  window.scrollTo({top: 0});
                }}
              />
            </>
          ) : (
            <>
              <h3>Sorry, there are no questions found.</h3>
              <p>
                Try changing or{' '}
                <Button onClick={clearFilters} type='text'>
                  clearing the filters
                </Button>
                .
              </p>
              <RecommendedQuestions trackingComponent='emptyQuestionList' />
            </>
          )}
        </div>
        {!isMobile && (
          <Sticky disabled={isMobile}>
            <div className={styles.sidebar}>
              <div className={styles.sidebarContent}>
                <h3>Quick filters</h3>
                <Divider />
                <QuickFilters config={config} onConfigChange={setConfig} />
                <Divider size='sm' />
                <QuestionWeekly />
                <Divider size='sm' />
                <h3>Top Companies</h3>
                <TextInput
                  name='sidebarTopCompanyQuery'
                  size='sm'
                  placeholder='Search for companies...'
                  icon={<SearchIcon color='greyMedium' />}
                  value={config.searchCompany}
                  onChange={(searchTag) => {
                    setConfig({...config, searchCompany: searchTag});
                  }}
                />

                <div className={styles.BillBox} data-sidebar='topCompanies'>
                  <TopCompanies
                    config={config}
                    onConfigChange={setConfig}
                    companyFilter={config.searchCompany}
                  />
                </div>

                <Divider size='sm' />
                <h3>Question Tags</h3>
                <TextInput
                  name='sidebarQuestionTagQuery'
                  size='sm'
                  placeholder='Search for questions tags...'
                  icon={<SearchIcon color='greyMedium' />}
                  value={config.searchQuestionTag}
                  onChange={(searchTag) => {
                    setConfig({...config, searchQuestionTag: searchTag});
                  }}
                />

                <div className={styles.BillBox} data-sidebar='questionTags'>
                  <TopQuestionTags
                    maxVisible={50}
                    questions={questions}
                    config={config}
                    onConfigChange={setConfig}
                    questionFilter={config.searchQuestionTag}
                  />
                </div>
              </div>
            </div>
          </Sticky>
        )}
      </div>
    </>
  );
};

type Props = {
  questions: Question[];
  categories: QuestionCategory[];
};
